import React, { useState, useEffect, useCallback } from "react";
import { Helmet } from "react-helmet";
import PageLayout, {
  PageSection,
} from "../../components/navigation/PageLayout";
import AuditFilters from "./components/AuditFilters";
import AuditEventTable from "./components/AuditEventTable";
import AuditEventDetail from "./components/AuditEventDetail";
import Pagination from "../../components/ui/Pagination";
import Icon from "../../components/AppIcon";
import { apiClient } from "../../services/api/client";
import { useTenantContext } from "../../contexts/TenantContext";

// Standard page size for all list views
const DEFAULT_PAGE_SIZE = 25;

const AuditTrail = () => {
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [entityTypes, setEntityTypes] = useState([]);
  const [summary, setSummary] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);

  // Pagination state - standard 25 records per page
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    totalCount: 0,
    totalPages: 0,
  });

  const [filters, setFilters] = useState({
    eventType: "all",
    dateRange: "last-7-days",
    entityType: "all",
    searchQuery: "",
    fromDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(),
    toDate: new Date().toISOString(),
  });

  // Tenant context
  const { selectedTenant, needsTenantSelection, isGlobalAdmin } =
    useTenantContext();

  // Only fetch data when tenant is selected (or for non-global admins)
  const shouldFetchData = !isGlobalAdmin || selectedTenant;

  // Fetch audit logs from API
  const fetchAuditLogs = useCallback(
    async (currentFilters, page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
      if (!shouldFetchData) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams();
        params.append("page", page.toString());
        params.append("pageSize", pageSize.toString());

        if (currentFilters.fromDate) {
          params.append("fromDate", currentFilters.fromDate);
        }
        if (currentFilters.toDate) {
          params.append("toDate", currentFilters.toDate);
        }
        if (currentFilters.entityType && currentFilters.entityType !== "all") {
          params.append("entityType", currentFilters.entityType);
        }
        if (currentFilters.eventType && currentFilters.eventType !== "all") {
          params.append("action", currentFilters.eventType);
        }
        if (currentFilters.searchQuery && currentFilters.searchQuery.trim()) {
          params.append("search", currentFilters.searchQuery.trim());
        }

        const response = await apiClient.get(`/auditlogs?${params.toString()}`);

        if (response.success && response.data) {
          const {
            items,
            page: currentPage,
            pageSize: returnedPageSize,
            totalCount,
            totalPages,
          } = response.data;
          setAuditEvents(items || []);
          setPagination({
            page: currentPage,
            pageSize: returnedPageSize,
            totalCount,
            totalPages,
          });
        } else {
          setAuditEvents([]);
        }
      } catch (err) {
        console.error("Audit API error:", err.message);
        setError("Failed to load audit logs. Please try again.");
        setAuditEvents([]);
      } finally {
        setLoading(false);
      }
    },
    [shouldFetchData],
  );

  // Fetch entity types for filter dropdown
  const fetchEntityTypes = useCallback(async () => {
    try {
      const response = await apiClient.get("/auditlogs/entity-types");
      if (response.success && response.data) {
        setEntityTypes(response.data);
      }
    } catch (err) {
      console.log("Could not fetch entity types:", err.message);
    }
  }, []);

  // Fetch summary statistics
  const fetchSummary = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);

      const response = await apiClient.get(
        `/auditlogs/summary?${params.toString()}`,
      );
      if (response.success && response.data) {
        setSummary(response.data);
      }
    } catch (err) {
      console.log("Could not fetch summary:", err.message);
    }
  }, [filters.fromDate, filters.toDate]);

  // Initial load
  useEffect(() => {
    if (shouldFetchData) {
      fetchAuditLogs(filters, 1, pagination.pageSize);
      fetchEntityTypes();
      fetchSummary();
    }
  }, [shouldFetchData, selectedTenant?.id]);

  // Refetch when filters applied
  const handleApplyFilters = (newFilters) => {
    setFilters(newFilters);
    fetchAuditLogs(newFilters, 1, pagination.pageSize);
    fetchSummary();
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchAuditLogs(filters, newPage, pagination.pageSize);
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize }));
    fetchAuditLogs(filters, 1, newPageSize);
  };

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    { label: "Audit Trail", path: "/audit-trail" },
  ];

  // CSV export helper - comprehensive with proper formatting
  const exportToCsv = (events, filenamePrefix) => {
    const headers = [
      "Timestamp",
      "Date",
      "Action",
      "Entity Type",
      "User",
      "Distribution Rule",
      "Amount",
      "Description",
    ];

    const rows = events.map((event) => {
      const date = new Date(event.timestamp).toLocaleDateString("en-GB");

      // Extract distribution rule name from new values for DistributionRule entities
      let ruleName = "";
      if (event.entityType === "DistributionRule") {
        try {
          if (event.newValues) {
            const newVals = JSON.parse(event.newValues);
            ruleName = newVals.Name || "";
          }
          // Fallback to parsing from title/description
          if (!ruleName && event.title) {
            // Extract rule name from "Create DistributionRule" or "Update DistributionRule" format
            const matches =
              event.title.match(/DistributionRule\s*-?\s*(.+)/) ||
              event.description.match(/Rule:\s*(.+?)[,\n]/) ||
              event.description.match(/'([^']+)'/) ||
              event.description.match(/"([^"]+)"/);
            ruleName = matches ? matches[1].trim() : "";
          }
        } catch (e) {
          // Silently fail
        }
      }

      // Extract amount for TRONC/Transaction/Allocation entities
      let amount = "";
      if (
        event.entityType.includes("Tronc") ||
        event.entityType.includes("Transaction") ||
        event.entityType.includes("Allocation")
      ) {
        try {
          if (event.newValues) {
            const newVals = JSON.parse(event.newValues);
            amount = newVals.Amount
              ? `�${parseFloat(newVals.Amount).toFixed(2)}`
              : "";
          }
        } catch (e) {
          // Silently fail
        }
      }

      return [
        new Date(event.timestamp).toLocaleString("en-GB"),
        date,
        event.action || "",
        event.entityType || "",
        event.performedBy || "System",
        ruleName,
        amount,
        event.reason || event.changedProperties || "",
      ];
    });

    // Properly escape CSV values - handle quotes, commas, newlines
    const escapeCell = (cell) => {
      const str = (cell ?? "").toString();
      if (
        str.includes(",") ||
        str.includes('"') ||
        str.includes("\n") ||
        str.includes("\r")
      ) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCell).join(","))
      .join("\r\n");

    // Add BOM for Excel compatibility with special characters
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${filenamePrefix}-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Export current page to CSV
  const handleExportCurrentPage = () => {
    if (auditEvents.length === 0) return;
    exportToCsv(auditEvents, `audit-trail-page-${pagination.page}`);
  };

  // Export all filtered results to CSV
  const handleExportAll = async () => {
    if (pagination.totalCount === 0) return;

    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("page", "1");
      params.append(
        "pageSize",
        Math.min(pagination.totalCount, 10000).toString(),
      );

      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);
      if (filters.entityType && filters.entityType !== "all") {
        params.append("entityType", filters.entityType);
      }
      if (filters.eventType && filters.eventType !== "all") {
        params.append("action", filters.eventType);
      }

      const response = await apiClient.get(`/auditlogs?${params.toString()}`);

      if (response.success && response.data?.items) {
        exportToCsv(
          response.data.items,
          `audit-trail-all-${pagination.totalCount}-records`,
        );
      }
    } catch (err) {
      console.error("Export error:", err);
    } finally {
      setExportLoading(false);
    }
  };

  // Transform API data to component format
  const transformedEvents = auditEvents.map((event) => ({
    id: event.id,
    eventType: event.action?.toLowerCase() || "info",
    eventCategory: event.entityType || "System",
    title: `${event.action} ${event.entityType}`,
    description: event.reason || event.changedProperties || "No description",
    user: {
      name: event.performedBy || "System",
      email: "",
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(event.performedBy || "System")}&background=1a365d&color=fff`,
    },
    timestamp: event.timestamp,
    entityId: event.entityId,
    source: event.source,
    oldValues: event.oldValues,
    newValues: event.newValues,
    changedProperties: event.changedProperties,
    correlationId: event.correlationId,
    severity:
      event.action === "Delete"
        ? "warning"
        : event.action === "Create"
          ? "success"
          : event.action === "Distributed"
            ? "success"
            : "info",
  }));

  // Apply client-side search filter
  const filteredEvents = transformedEvents.filter((event) => {
    if (
      filters.searchQuery &&
      !event.title.toLowerCase().includes(filters.searchQuery.toLowerCase()) &&
      !event.description
        .toLowerCase()
        .includes(filters.searchQuery.toLowerCase()) &&
      !(event.entityId || "")
        .toLowerCase()
        .includes(filters.searchQuery.toLowerCase())
    ) {
      return false;
    }
    return true;
  });

  // Stats from summary
  const stats = {
    total: summary?.totalEvents || pagination.totalCount,
    last24h: summary?.last24Hours || 0,
    changes: (summary?.updateCount || 0) + (summary?.distributionCount || 0),
  };

  // Select Tenant Empty State for Global Admins
  if (isGlobalAdmin && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>Audit Trail - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Audit Trail"
          description="Complete history of distribution changes, rule modifications, and approvals."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
              to view their audit trail.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="ArrowLeft" size={16} />
              <span>Use the tenant selector in the sidebar</span>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Audit Trail - {selectedTenant?.name || "Rocket Tronc"}</title>
        <meta
          name="description"
          content="Complete history of distribution changes, rule modifications, and approvals"
        />
      </Helmet>

      <PageLayout
        title="Audit Trail"
        description={
          selectedTenant
            ? `Viewing ${selectedTenant.name} - Complete history of changes and approvals.`
            : "Complete history of distribution changes, rule modifications, and approvals."
        }
        breadcrumbs={breadcrumbs}
      >
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="card p-4">
            <p className="text-sm font-medium text-muted-foreground mb-1">
              Total Events (Filtered)
            </p>
            <p className="text-2xl font-bold font-data text-foreground">
              {pagination.totalCount.toLocaleString()}
            </p>
          </div>
          <div className="card p-4">
            <p className="text-sm font-medium text-muted-foreground mb-1">
              Last 24 Hours
            </p>
            <p className="text-2xl font-bold font-data text-success">
              {stats.last24h}
            </p>
          </div>
          <div className="card p-4">
            <p className="text-sm font-medium text-muted-foreground mb-1">
              Updates & Distributions
            </p>
            <p className="text-2xl font-bold font-data text-warning">
              {stats.changes}
            </p>
          </div>
          <div className="card p-4">
            <p className="text-sm font-medium text-muted-foreground mb-1">
              Current Page
            </p>
            <p className="text-2xl font-bold font-data text-primary">
              {pagination.page} / {pagination.totalPages || 1}
            </p>
          </div>
        </div>

        {/* Filters */}
        <AuditFilters
          filters={filters}
          onFilterChange={setFilters}
          entityTypes={entityTypes}
          onApplyFilters={handleApplyFilters}
        />

        {/* Loading State */}
        {loading && (
          <div className="card p-8 text-center mb-6">
            <div className="spinner mx-auto mb-4" />
            <p className="text-muted-foreground">Loading audit logs...</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="alert alert-warning mb-6">
            <Icon name="AlertTriangle" size={20} />
            <div>
              <p className="alert-title">Unable to load audit logs</p>
              <p className="alert-description">{error}</p>
            </div>
            <button
              className="btn btn-outline btn-sm ml-auto"
              onClick={() =>
                fetchAuditLogs(filters, pagination.page, pagination.pageSize)
              }
            >
              <Icon name="RefreshCw" size={16} />
              Retry
            </button>
          </div>
        )}

        {/* Content */}
        {!loading && (
          <>
            {filteredEvents.length === 0 ? (
              <div className="card p-8 text-center">
                <div className="empty-state-icon mx-auto mb-4">
                  <Icon name="FileText" size={32} />
                </div>
                <h3 className="text-lg font-semibold mb-2">
                  No audit events found
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  {auditEvents.length === 0
                    ? "Audit events will appear here as you make changes to employees, distributions, and rules."
                    : "No events match your current filters. Try adjusting your search criteria."}
                </p>
              </div>
            ) : (
              <>
                {/* Main Content */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <PageSection title="Events">
                      <AuditEventTable
                        events={filteredEvents}
                        selectedEventId={selectedEvent?.id}
                        onSelectEvent={setSelectedEvent}
                      />

                      {/* Pagination with Export */}
                      <Pagination
                        page={pagination.page}
                        pageSize={pagination.pageSize}
                        totalCount={pagination.totalCount}
                        totalPages={pagination.totalPages}
                        onPageChange={handlePageChange}
                        onPageSizeChange={handlePageSizeChange}
                        onExportCurrentPage={handleExportCurrentPage}
                        onExportAll={handleExportAll}
                        exportLoading={exportLoading}
                        exportDisabled={filteredEvents.length === 0}
                        className="mt-4"
                      />
                    </PageSection>
                  </div>

                  <div className="lg:col-span-1 lg:mt-[52px]">
                    {selectedEvent ? (
                      <AuditEventDetail
                        event={selectedEvent}
                        onClose={() => setSelectedEvent(null)}
                      />
                    ) : (
                      <div className="card p-8 text-center sticky top-20">
                        <div className="empty-state-icon mx-auto mb-4">
                          <Icon name="Info" size={24} />
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Select an event to view details
                        </p>

                        {/* Entity breakdown */}
                        {summary?.entityTypeBreakdown &&
                          Object.keys(summary.entityTypeBreakdown).length >
                            0 && (
                            <div className="mt-6 text-left">
                              <h4 className="font-semibold mb-3 text-sm">
                                Events by Entity Type
                              </h4>
                              <div className="space-y-2">
                                {Object.entries(summary.entityTypeBreakdown)
                                  .sort((a, b) => b[1] - a[1])
                                  .slice(0, 8)
                                  .map(([type, count]) => (
                                    <div
                                      key={type}
                                      className="flex justify-between text-sm"
                                    >
                                      <span className="text-muted-foreground">
                                        {type}
                                      </span>
                                      <span className="font-medium">
                                        {count}
                                      </span>
                                    </div>
                                  ))}
                              </div>
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </PageLayout>
    </>
  );
};

export default AuditTrail;
